/**
 * Single message component
 */

import { useCallback, useRef, useState, type CSSProperties } from 'react';
import {
  DEFAULT_OUTPUT_TEXT_ZOOM,
  OUTPUT_TEXT_ZOOM_MAX,
  OUTPUT_TEXT_ZOOM_MIN,
  OUTPUT_TEXT_ZOOM_STEP,
  type Attachment,
  type ContainerFileCitation,
  type Message as MessageType,
  type MessageRenderMode,
} from '../../types';
import type { JsonPanelData } from '../JsonSidePanel';
import { ReasoningBox } from '../ReasoningBox';
import { ToolCallBox } from '../ToolCallBox';
import { MessageContent } from './MessageContent';
import { useSettingsContext } from '../../context/SettingsContext';
import { usePinchZoom } from '../../hooks/usePinchZoom';
import { isImageAttachment } from '../../utils/attachment';
import { triggerContainerFileDownload } from '../../utils/api';
import './Message.css';

interface MessageProps {
  /** Message data to display */
  message: MessageType;
  /** Handler to open JSON panel */
  onOpenJsonPanel: (data: JsonPanelData) => void;
  /** Handler when user approves an MCP tool call */
  onMcpApprove?: (approvalRequestId: string) => void;
  /** Handler when user denies an MCP tool call */
  onMcpDeny?: (approvalRequestId: string) => void;
  /** Handler when user retries a failed message */
  onRetry?: (messageId: string) => void;
  /** Whether a message is currently streaming — disables the retry button to prevent race conditions */
  isStreaming?: boolean;
}

interface RenderModeToggleProps {
  /** Current effective render mode */
  currentMode: MessageRenderMode;
  /** Handler when mode changes */
  onModeChange: (mode: MessageRenderMode | null) => void;
  /** Whether a per-message override is active */
  hasOverride: boolean;
}

/**
 * Renders attachment thumbnails in a message
 */
function MessageAttachments({ attachments }: { attachments: Attachment[] }) {
  return (
    <div className="message__attachments">
      {attachments.map((attachment) => (
        <div key={attachment.id} className={`message__attachment ${attachment.uploadStatus === 'uploading' ? 'message__attachment--uploading' : ''} ${attachment.uploadStatus === 'failed' ? 'message__attachment--failed' : ''}`}>
          {isImageAttachment(attachment) && attachment.previewUrl ? (
            <img
              src={attachment.previewUrl}
              alt={attachment.name}
              className="message__attachment-image"
            />
          ) : (
            <div className="message__attachment-file">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="message__attachment-file-icon"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <span className="message__attachment-filename" title={attachment.name}>
                {attachment.name}
              </span>
            </div>
          )}
          {attachment.uploadStatus === 'uploading' && (
            <div className="message__attachment-upload-indicator">
              <div className="message__attachment-spinner" aria-label="Uploading" />
            </div>
          )}
          {attachment.uploadStatus === 'failed' && (
            <div className="message__attachment-upload-indicator message__attachment-upload-indicator--failed">
              <span aria-label="Upload failed">✕</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Toggle buttons for render mode selection
 */
function RenderModeToggle({ currentMode, onModeChange, hasOverride }: RenderModeToggleProps) {
  const modes: { mode: MessageRenderMode; label: string; title: string }[] = [
    { mode: 'markdown', label: 'MD', title: 'Rendered Markdown' },
    { mode: 'plaintext', label: 'TXT', title: 'Plain Text' },
    { mode: 'code', label: '</>', title: 'Code Block' },
  ];

  return (
    <div className="message__render-toggle">
      {modes.map(({ mode, label, title }) => (
        <button
          key={mode}
          className={`message__render-toggle-btn ${currentMode === mode ? 'message__render-toggle-btn--active' : ''}`}
          onClick={() => onModeChange(mode)}
          title={title}
          aria-label={title}
          aria-pressed={currentMode === mode}
        >
          {label}
        </button>
      ))}
      {hasOverride && (
        <button
          className="message__render-toggle-reset"
          onClick={() => onModeChange(null)}
          title="Reset to global setting"
          aria-label="Reset to global setting"
        >
          ↺
        </button>
      )}
    </div>
  );
}

interface GeneratedFilesProps {
  /** Container file citations to display */
  files: ContainerFileCitation[];
  /** Handler to download a file */
  onDownload: (file: ContainerFileCitation) => void;
  /** Set of file IDs currently being downloaded */
  downloadingFiles: Set<string>;
  /** Set of file IDs whose last download attempt failed */
  failedDownloads: Set<string>;
}

/**
 * Displays downloadable files generated by code interpreter
 */
function GeneratedFiles({ files, onDownload, downloadingFiles, failedDownloads }: GeneratedFilesProps) {
  return (
    <div className="message__generated-files">
      <div className="message__generated-files-header">Generated Files</div>
      <ul className="message__generated-files-list">
        {files.map((file) => {
          const isDownloading = downloadingFiles.has(file.fileId);
          const hasFailed = failedDownloads.has(file.fileId);
          return (
            <li key={file.fileId} className="message__generated-file">
              <button
                className={`message__generated-file-button${hasFailed ? ' message__generated-file-button--error' : ''}`}
                onClick={() => onDownload(file)}
                disabled={isDownloading}
                title={hasFailed ? `Download failed – click to retry ${file.filename}` : `Download ${file.filename}`}
                aria-label={hasFailed ? `Download failed – click to retry ${file.filename}` : `Download ${file.filename}`}
              >
                <span className="message__generated-file-icon" aria-hidden="true">
                  {isDownloading ? '⏳' : hasFailed ? '❌' : '📥'}
                </span>
                <span className="message__generated-file-name">{file.filename}</span>
                {hasFailed && (
                  <span className="message__generated-file-error">Download failed</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface MessageOutputStyle extends CSSProperties {
  '--message-output-scale'?: string;
}

/**
 * Renders a single chat message with appropriate styling based on role
 */
export function Message({ message, onOpenJsonPanel, onMcpApprove, onMcpDeny, onRetry, isStreaming = false }: MessageProps) {
  const { settings, updateSettings } = useSettingsContext();
  const [overrideRenderMode, setOverrideRenderMode] = useState<MessageRenderMode | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [failedDownloads, setFailedDownloads] = useState<Set<string>>(new Set());
  const assistantContentRef = useRef<HTMLDivElement | null>(null);
  
  const isUser = message.role === 'user';
  const shouldEnableOutputZoom = !isUser && !message.isError;
  const hasReasoning = message.reasoning && message.reasoning.length > 0;
  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0;
  const hasCitations = message.citations && message.citations.length > 0;
  const hasFileCitations = message.fileCitations && message.fileCitations.length > 0;
  const hasContainerFileCitations = message.containerFileCitations && message.containerFileCitations.length > 0;
  const hasAttachments = message.attachments && message.attachments.length > 0;
  const showThinking = message.isStreaming && !message.content && !hasReasoning;
  const isTruncated = message.isTruncated ?? false;
  
  // Effective render mode: per-message override > global setting > default
  const globalRenderMode = settings.messageRenderMode ?? 'markdown';
  const effectiveRenderMode = overrideRenderMode ?? globalRenderMode;
  const outputTextZoom = settings.outputTextZoom ?? DEFAULT_OUTPUT_TEXT_ZOOM;
  
  // Only show reset button if override differs from global
  const hasOverride = overrideRenderMode !== null && overrideRenderMode !== globalRenderMode;
  
  // Determine if JSON data is available
  const jsonData = isUser ? message.requestJson : message.responseJson;
  const hasJsonData = !!jsonData;

  const handleJsonClick = useCallback(() => {
    if (jsonData) {
      onOpenJsonPanel({
        title: isUser ? 'Request JSON' : 'Response JSON',
        data: jsonData,
      });
    }
  }, [jsonData, isUser, onOpenJsonPanel]);

  const handleRenderModeChange = useCallback((mode: MessageRenderMode | null) => {
    setOverrideRenderMode(mode);
  }, []);

  const handleOutputTextZoomChange = useCallback((nextZoom: number) => {
    if (nextZoom !== outputTextZoom) {
      updateSettings({ outputTextZoom: nextZoom });
    }
  }, [outputTextZoom, updateSettings]);

  const handleCopyContent = useCallback(async () => {
    if (message.content) {
      try {
        await navigator.clipboard.writeText(message.content);
      } catch {
        // Clipboard write failed silently
      }
    }
  }, [message.content]);

  const handleFileDownload = useCallback(async (file: ContainerFileCitation) => {
    // Mark file as downloading
    setDownloadingFiles((prev) => new Set(prev).add(file.fileId));
    
    // Clear any previous error for this file
    setFailedDownloads((prev) => { const next = new Set(prev); next.delete(file.fileId); return next; });
    
    try {
      await triggerContainerFileDownload(
        { endpoint: settings.endpoint, apiKey: settings.apiKey },
        file.containerId,
        file.fileId,
        file.filename
      );
    } catch (err) {
      console.error('Failed to download file:', err);
      setFailedDownloads((prev) => new Set(prev).add(file.fileId));
    } finally {
      // Remove from downloading state
      setDownloadingFiles((prev) => {
        const next = new Set(prev);
        next.delete(file.fileId);
        return next;
      });
    }
  }, [settings.endpoint, settings.apiKey]);

  usePinchZoom(assistantContentRef, {
    enabled: shouldEnableOutputZoom,
    value: outputTextZoom,
    min: OUTPUT_TEXT_ZOOM_MIN,
    max: OUTPUT_TEXT_ZOOM_MAX,
    step: OUTPUT_TEXT_ZOOM_STEP,
    onChange: handleOutputTextZoomChange,
  });

  const assistantContentStyle: MessageOutputStyle | undefined = shouldEnableOutputZoom
    ? { '--message-output-scale': `${outputTextZoom / DEFAULT_OUTPUT_TEXT_ZOOM}` }
    : undefined;

  return (
    <div
      className={`message ${isUser ? 'message--user' : 'message--assistant'} ${
        message.isError ? 'message--error' : ''
      }`}
    >
      <div className="message__header">
        <div className="message__header-left">
          <span className="message__role">{isUser ? 'You' : 'Assistant'}</span>
          <span className="message__timestamp">
            {message.timestamp instanceof Date
              ? message.timestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
              : new Date(message.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>
        <div className="message__header-actions">
          {/* Render mode toggle - only for assistant messages, not errors */}
          {!isUser && !message.isError && (
            <RenderModeToggle
              currentMode={effectiveRenderMode}
              onModeChange={handleRenderModeChange}
              hasOverride={hasOverride}
            />
          )}
          {/* JSON and action buttons for error messages */}
          {!isUser && message.isError && (
            <>
              {hasJsonData && (
                <button
                  className="message__json-button"
                  onClick={handleJsonClick}
                  aria-label="View JSON"
                  title="View response JSON"
                  disabled={isStreaming}
                >
                  {'{ }'}
                </button>
              )}
              {onRetry && (
                <button
                  className="message__retry-button"
                  onClick={() => onRetry(message.id)}
                  aria-label="Retry"
                  title="Retry"
                  disabled={isStreaming}
                >
                  ↺ Retry
                </button>
              )}
            </>
          )}
          {/* JSON and Copy buttons - always render both for assistant to avoid layout shift */}
          {!isUser && !message.isError && (
            <>
              <button
                className={`message__json-button ${!hasJsonData ? 'message__json-button--hidden' : ''}`}
                onClick={handleJsonClick}
                aria-label="View JSON"
                title="View response JSON"
                disabled={!hasJsonData}
              >
                {'{ }'}
              </button>
              <button
                className={`message__copy-button ${!message.content || message.isStreaming ? 'message__copy-button--hidden' : ''}`}
                onClick={handleCopyContent}
                aria-label="Copy message"
                title="Copy to clipboard"
                disabled={!message.content || message.isStreaming}
              >
                📋
              </button>
            </>
          )}
          {/* JSON and Copy buttons for user messages */}
          {isUser && (
            <>
              <button
                className={`message__json-button ${!hasJsonData ? 'message__json-button--hidden' : ''}`}
                onClick={handleJsonClick}
                aria-label="View JSON"
                title="View request JSON"
                disabled={!hasJsonData}
              >
                {'{ }'}
              </button>
              <button
                className={`message__copy-button ${!message.content ? 'message__copy-button--hidden' : ''}`}
                onClick={handleCopyContent}
                aria-label="Copy message"
                title="Copy to clipboard"
                disabled={!message.content}
              >
                📋
              </button>
            </>
          )}
        </div>
      </div>
      <div className="message__content-wrapper">
        {/* Attachments (for user messages) */}
        {hasAttachments && <MessageAttachments attachments={message.attachments!} />}

        {/* Reasoning box (before content) */}
        {hasReasoning && (
          <ReasoningBox
            reasoning={message.reasoning!}
            isStreaming={message.isStreaming}
          />
        )}

        {/* Tool calls */}
        {hasToolCalls &&
          message.toolCalls!.map((toolCall) => (
            <ToolCallBox
              key={toolCall.id}
              toolCall={toolCall}
              onApprove={onMcpApprove}
              onDeny={onMcpDeny}
              fileCitations={toolCall.type === 'file_search' ? message.fileCitations : undefined}
            />
          ))}

        {/* Main content or thinking indicator */}
        {showThinking ? (
          <div className="message__thinking">
            <span className="message__thinking-dot">●</span>
            <span className="message__thinking-dot">●</span>
            <span className="message__thinking-dot">●</span>
            Thinking...
          </div>
        ) : (
          <div
            className={`message__content ${shouldEnableOutputZoom ? 'message__content--zoomable' : ''}`}
            ref={shouldEnableOutputZoom ? assistantContentRef : undefined}
            style={assistantContentStyle}
          >
            {isUser || message.isError ? (
              <>
                {message.content}
                {message.errorCode && (
                  <div className="message__error-code">
                    Error code: <code>{message.errorCode}</code>
                  </div>
                )}
              </>
            ) : (
              // Assistant messages use render mode
              <MessageContent
                content={message.content}
                renderMode={effectiveRenderMode}
                containerFileCitations={message.containerFileCitations}
                onDownloadFile={handleFileDownload}
              />
            )}
            {/* Stopped indicator - inline when there's content */}
            {message.isStopped && message.content && (
              <span className="message__cancelled"> stopped</span>
            )}
          </div>
        )}

        {/* Stopped indicator - standalone when no content */}
        {message.isStopped && !message.content && (
          <div className="message__cancelled message__cancelled--standalone">stopped</div>
        )}

        {/* Truncated indicator */}
        {isTruncated && !message.isStreaming && (
          <div className="message__truncated">
            <span className="message__truncated-icon" aria-hidden="true">⚠️</span>
            <span className="message__truncated-text">
              Response truncated{message.truncationReason === 'max_output_tokens' ? ' (max tokens reached)' : ''}
            </span>
          </div>
        )}

        {/* Streaming cursor */}
        {message.isStreaming && message.content && (
          <span className="message__cursor">▌</span>
        )}

        {/* Citations from web search */}
        {hasCitations && !message.isStreaming && (
          <div className="message__citations">
            <div className="message__citations-header">Sources</div>
            <ul className="message__citations-list">
              {message.citations!.map((citation) => {
                const isSafeUrl = /^https?:\/\//i.test(citation.url);
                return (
                  <li key={citation.url} className="message__citation">
                    {isSafeUrl ? (
                      <a
                        href={citation.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="message__citation-link"
                        title={citation.url}
                      >
                        {citation.title || citation.url}
                      </a>
                    ) : (
                      <span className="message__citation-link" title={citation.url}>
                        {citation.title || citation.url}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Citations from file search */}
        {hasFileCitations && !message.isStreaming && (
          <div className="message__citations message__citations--files">
            <div className="message__citations-header">Files Referenced</div>
            <ul className="message__citations-list">
              {message.fileCitations!.map((citation) => (
                <li key={citation.fileId} className="message__citation message__citation--file">
                  <span className="message__citation-file-icon" aria-hidden="true">📄</span>
                  <span className="message__citation-filename" title={citation.filename}>
                    {citation.filename}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Generated files from code interpreter */}
        {hasContainerFileCitations && !message.isStreaming && (
          <GeneratedFiles
            files={message.containerFileCitations!}
            onDownload={handleFileDownload}
            downloadingFiles={downloadingFiles}
            failedDownloads={failedDownloads}
          />
        )}
      </div>
    </div>
  );
}
